import { BaseApiService } from '@doccareplus/api-client';
import { transformAuthResponse, type AuthResponse, type RawAuthResponse } from './auth.service';

/**
 * How the clinic reached the invitee. Token possession is the contact proof, so
 * this is display-only — there is no OTP step in the accept flow.
 */
export type InvitationContactMethod = 'Email' | 'Phone';

/**
 * Response of GET /invitations/preview?token=… — everything the public accept
 * screen needs to render before the invitee commits to anything.
 */
export interface InvitationPreview {
  invitationId: string;
  clinicName: string;
  inviterName: string;
  contactMethod: InvitationContactMethod;
  contactValue: string;
  intendedRoleNames: string[];
  isProvider: boolean;
  expiresAt: string;
  /**
   * True when the invited contact already has a DocCare+ account. Such invitees
   * accept while signed in and are never asked for a password.
   */
  accountExists: boolean;
}

/**
 * Body of POST /invitations/accept. `password`, `firstName`, and `lastName` are required when the
 * preview reported `accountExists: false`, and must be omitted otherwise (that
 * path requires the caller to be signed in as the invited account).
 */
export interface AcceptInvitationRequest {
  token: string;
  password?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

/**
 * Result of POST /invitations/accept. The raw `session` payload the backend
 * returns is identical to POST /auth/login and is transformed here, so callers
 * get the same `{ user, tokens, memberships }` shape `authApi.login` returns.
 */
export interface AcceptInvitationResult {
  clinicId: string;
  membershipId: string;
  status: string;
  assignedRoleNames: string[];
  session: AuthResponse;
}

interface RawAcceptInvitationResponse {
  clinicId: string;
  membershipId: string;
  status: string;
  assignedRoleNames: string[];
  session: RawAuthResponse;
}

/**
 * Invitation API Service — the anonymous-capable staff-invitation endpoints
 * backing the public `/invitations/accept?token=…` link.
 */
class InvitationApiService extends BaseApiService {
  constructor() {
    super('/invitations');
  }

  /** Read the invitation behind an opaque token (unknown/consumed → 404, expired → 400). */
  preview(token: string): Promise<InvitationPreview> {
    return this.get<InvitationPreview>('/preview', { params: { token } });
  }

  /** Accept the invitation; returns the joined membership plus a usable session. */
  async accept(body: AcceptInvitationRequest): Promise<AcceptInvitationResult> {
    const raw = await this.post<RawAcceptInvitationResponse, AcceptInvitationRequest>('/accept', body);
    return { ...raw, session: transformAuthResponse(raw.session) };
  }

  /** Decline the invitation. Works signed-in or anonymous — token possession is the proof. */
  decline(token: string): Promise<boolean> {
    return this.post<boolean, { token: string }>('/decline', { token });
  }
}

// Create singleton instance
export const invitationApi = new InvitationApiService();
